/*
 * ============================================================
 * MODI — Salon scroll (<modi-salon-scroll>)
 * A scroll-scrubbed, sticky "salon sequence" stage.
 *
 * Supported media modes (data-mode):
 *   chapters    — crossfade between per-chapter images (default; no
 *                 frame sequence required). Uses the chapter blocks.
 *   static      — one fixed image; chapter captions advance on scroll.
 *   video_loop  — autoplay muted loop; captions advance on scroll.
 *   video_scrub — a muted video whose currentTime tracks scroll.
 *   sequence    — a numbered AVIF/WebP frame sequence (Higgsfield),
 *                 drawn to <canvas>, progressively loaded.
 *
 * Lifecycle: a custom element, so Shopify theme-editor section
 * load/unload is handled automatically by connected/disconnected
 * callbacks. Scroll work only runs while the stage is on screen
 * (IntersectionObserver) and is throttled with requestAnimationFrame.
 * Honours prefers-reduced-motion: when set, NO pinning/scrubbing —
 * CSS falls back to a plain stacked layout and this script no-ops.
 * ============================================================
 */
(function () {
  'use strict';

  if (customElements.get('modi-salon-scroll')) return;

  var MOBILE_MAX = 749;
  var MOBILE_FRAME_CAP = 48; // never load more than ~this many frames on phones

  class ModiSalonScroll extends HTMLElement {
    connectedCallback() {
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.mode = this.dataset.mode || 'chapters';
      this.chapters = Array.prototype.slice.call(this.querySelectorAll('.modi-salon__chapter'));
      this.steps = Math.max(1, this.chapters.length);
      this._activeIndex = -1;
      this._rafId = null;
      this._inView = false;

      // Reduced motion: let CSS present the stacked fallback; do nothing.
      if (this.reduced) {
        this._setActive(0);
        return;
      }

      // Only now do we take over: this class gates ALL pinning/crossfade CSS,
      // so without JS (or with reduced motion) the section stays a plain,
      // fully-visible stacked layout — no essential content is ever hidden.
      this.classList.add('modi-salon--active');

      this._onScrollOrResize = this._schedule.bind(this);

      this._io = new IntersectionObserver(
        function (entries) {
          var entry = entries[0];
          this._inView = entry.isIntersecting;
          if (this._inView) {
            this._bind();
            this._schedule();
          } else {
            this._unbind();
          }
        }.bind(this),
        { rootMargin: '300px 0px 300px 0px' }
      );
      this._io.observe(this);

      if (this.mode === 'sequence') this._initSequence();
      if (this.mode === 'video_scrub') this._initVideoScrub();

      this._update();
    }

    disconnectedCallback() {
      this._unbind();
      if (this._io) this._io.disconnect();
      if (this._rafId) cancelAnimationFrame(this._rafId);
      // Release decoded frames so memory is reclaimed on section reload.
      if (this._images) this._images.length = 0;
    }

    _bind() {
      // Capture-phase scroll: catches the page scroll whether it happens on
      // `window` OR inside an overflow container (Horizon scrolls a
      // `.page-wrapper`, so a plain window 'scroll' listener never fires).
      window.addEventListener('scroll', this._onScrollOrResize, { passive: true, capture: true });
      window.addEventListener('resize', this._onScrollOrResize);
    }

    _unbind() {
      window.removeEventListener('scroll', this._onScrollOrResize, { capture: true });
      window.removeEventListener('resize', this._onScrollOrResize);
    }

    _schedule() {
      if (this._rafId) return;
      this._rafId = requestAnimationFrame(
        function () {
          this._rafId = null;
          this._update();
        }.bind(this)
      );
    }

    /* Scroll progress 0..1 across the (taller-than-viewport) stage. */
    _progress() {
      var rect = this.getBoundingClientRect();
      var travel = rect.height - window.innerHeight;
      if (travel <= 0) return 0;
      var p = -rect.top / travel;
      return p < 0 ? 0 : p > 1 ? 1 : p;
    }

    _update() {
      var p = this._progress();
      this.style.setProperty('--modi-scroll-progress', p.toFixed(4));

      var idx = Math.min(this.steps - 1, Math.floor(p * this.steps));
      if (idx !== this._activeIndex) this._setActive(idx);

      if (this.mode === 'sequence') this._drawFrame(p);
      if (this.mode === 'video_scrub') this._seek(p);
    }

    _setActive(idx) {
      this._activeIndex = idx;
      for (var i = 0; i < this.chapters.length; i++) {
        this.chapters[i].classList.toggle('is-active', i === idx);
      }
    }

    /* ---- Frame sequence (Higgsfield-ready) ---- */
    _initSequence() {
      var data = this.querySelector('.modi-salon__frames-data');
      var frames = [];
      try {
        frames = data ? JSON.parse(data.textContent) : [];
      } catch (e) {
        frames = [];
      }
      // Cap frame count + density on mobile to protect data + memory.
      if (window.innerWidth <= MOBILE_MAX && frames.length > MOBILE_FRAME_CAP) {
        var step = Math.ceil(frames.length / MOBILE_FRAME_CAP);
        frames = frames.filter(function (_, i) {
          return i % step === 0;
        });
      }
      this._frames = frames;
      this._images = new Array(frames.length);
      this._canvas = this.querySelector('.modi-salon__canvas');
      this._ctx = this._canvas ? this._canvas.getContext('2d') : null;
      this._sizeCanvas();
      // Load the first frame eagerly so the stage is never blank.
      this._loadFrame(0, true);
    }

    _sizeCanvas() {
      if (!this._canvas) return;
      var dpr = Math.min(2, window.devicePixelRatio || 1);
      var w = this._canvas.clientWidth || this.clientWidth;
      var h = this._canvas.clientHeight || window.innerHeight;
      this._canvas.width = Math.round(w * dpr);
      this._canvas.height = Math.round(h * dpr);
    }

    _loadFrame(i, draw) {
      if (i < 0 || !this._frames || i >= this._frames.length || this._images[i]) return;
      var img = new Image();
      img.decoding = 'async';
      var self = this;
      img.onload = function () {
        self._images[i] = img;
        if (draw) self._paint(img);
      };
      img.src = this._frames[i];
    }

    _drawFrame(p) {
      if (!this._frames || !this._frames.length) return;
      var i = Math.min(this._frames.length - 1, Math.round(p * (this._frames.length - 1)));
      // Progressively load a small window around the current frame.
      var lookahead = 10;
      for (var j = Math.max(0, i - 2); j < Math.min(this._frames.length, i + lookahead); j++) {
        if (!this._images[j]) this._loadFrame(j, false);
      }
      var img = this._images[i];
      if (img) this._paint(img);
    }

    _paint(img) {
      if (!this._ctx || !this._canvas) return;
      var c = this._canvas;
      // object-fit: cover
      var cw = c.width;
      var ch = c.height;
      var ir = img.width / img.height;
      var cr = cw / ch;
      var dw, dh, dx, dy;
      if (ir > cr) {
        dh = ch;
        dw = ch * ir;
        dx = (cw - dw) / 2;
        dy = 0;
      } else {
        dw = cw;
        dh = cw / ir;
        dx = 0;
        dy = (ch - dh) / 2;
      }
      this._ctx.drawImage(img, dx, dy, dw, dh);
    }

    /* ---- Scrubbed video ---- */
    _initVideoScrub() {
      this._video = this.querySelector('.modi-salon__video');
      if (this._video) {
        this._video.removeAttribute('autoplay');
        this._video.pause();
      }
    }

    _seek(p) {
      if (this._video && this._video.duration && isFinite(this._video.duration)) {
        try {
          this._video.currentTime = p * this._video.duration;
        } catch (e) {
          /* seeking not ready yet */
        }
      }
    }
  }

  customElements.define('modi-salon-scroll', ModiSalonScroll);
})();
